import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { E2E_USER_HOME } from '@/constants/e2eSchedulingTestIds';
import { cn } from '@/lib/utils';
import { userHomeDescriptionAccentLinkClassName } from './userHomeCardStyles';
import {
	UserHomeUpcomingSessionCard,
	UserHomeUpcomingSessionCardSkeleton,
} from './UserHomeUpcomingSessionCard';

type UserHomeUpcomingSessionsCarouselProps = {
	sessions: Sessions.Session[];
	isLoading: boolean;
	currentUserId?: string;
	onPayment: (session: Sessions.Session) => void;
};

const CARD_GAP_PX = 16;
const CARD_SLIDE_CLASS = 'w-[min(100%,400px)] shrink-0 snap-start';

const UpcomingSessionsSectionHeader = ({
	trailing,
	onViewAllConsultas,
	onSchedule,
}: {
	trailing?: React.ReactNode;
	onViewAllConsultas: () => void;
	onSchedule: () => void;
}) => (
	<div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
		<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
			<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
				<Calendar size={24} />
			</div>
			<div className="space-y-1">
				<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
					Próximas sessões
				</h2>
				<p className="text-sm text-zinc-500">
					Acompanhe suas consultas agendadas.{' '}
					<button
						type="button"
						onClick={onViewAllConsultas}
						className={userHomeDescriptionAccentLinkClassName}
					>
						Ver todas as minhas consultas
						<ChevronRight
							size={14}
							className="transition-transform group-hover:translate-x-0.5"
							aria-hidden
						/>
					</button>
				</p>
			</div>
		</div>
		<div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
			<div
				data-site-tutorial="home-schedule-button"
				className="w-full shrink-0 sm:w-auto"
			>
				<Button
					text="Agendar"
					data-testid={E2E_USER_HOME.SCHEDULE_BUTTON}
					className="w-full justify-center rounded-full bg-emerald-600 px-5 py-2 font-black uppercase text-white sm:w-auto"
					startIcon={<Plus className="size-4" />}
					onClick={onSchedule}
				/>
			</div>
			{trailing ? (
				<div className="flex items-center justify-end">{trailing}</div>
			) : null}
		</div>
	</div>
);

const getActiveSlideIndex = (el: HTMLDivElement, count: number): number => {
	if (count <= 1) return 0;

	const maxScrollLeft = el.scrollWidth - el.clientWidth;
	const scrollLeft = el.scrollLeft;

	if (scrollLeft <= 4) return 0;
	if (scrollLeft >= maxScrollLeft - 4) return count - 1;

	const viewportCenter = scrollLeft + el.clientWidth / 2;
	const slides = Array.from(el.children) as HTMLElement[];

	let bestIndex = 0;
	let bestDistance = Infinity;

	slides.forEach((slide, index) => {
		const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
		const distance = Math.abs(slideCenter - viewportCenter);
		if (distance < bestDistance) {
			bestDistance = distance;
			bestIndex = index;
		}
	});

	return bestIndex;
};

const UserHomeUpcomingSessionsCarousel = ({
	sessions,
	isLoading,
	onPayment,
}: UserHomeUpcomingSessionsCarouselProps) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const scrollRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [hasOverflow, setHasOverflow] = useState(false);
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);

	const getSlideStride = useCallback(() => {
		const el = scrollRef.current;
		if (!el?.firstElementChild) return 0;
		return el.firstElementChild.clientWidth + CARD_GAP_PX;
	}, []);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;

		const maxScrollLeft = el.scrollWidth - el.clientWidth;
		const overflow = maxScrollLeft > 4;
		setHasOverflow(overflow);
		setCanScrollPrev(overflow && el.scrollLeft > 4);
		setCanScrollNext(overflow && el.scrollLeft < maxScrollLeft - 4);

		setActiveIndex(getActiveSlideIndex(el, sessions.length));
	}, [sessions.length]);

	useEffect(() => {
		setActiveIndex(0);
		scrollRef.current?.scrollTo({ left: 0 });
	}, [sessions.length]);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const handleScroll = () => updateScrollState();
		el.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', handleScroll);

		const resizeObserver = new ResizeObserver(handleScroll);
		resizeObserver.observe(el);
		Array.from(el.children).forEach((child) =>
			resizeObserver.observe(child),
		);

		return () => {
			el.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
			resizeObserver.disconnect();
		};
	}, [updateScrollState, sessions.length]);

	useEffect(() => {
		updateScrollState();
	}, [sessions.length, updateScrollState]);

	const scrollToIndex = (index: number) => {
		const el = scrollRef.current;
		if (!el) return;

		const slide = el.children[index] as HTMLElement | undefined;
		if (!slide) return;

		el.scrollTo({
			left: slide.offsetLeft,
			behavior: 'smooth',
		});
	};

	const scrollByStep = (direction: 'prev' | 'next') => {
		const el = scrollRef.current;
		const stride = getSlideStride();
		if (!el || stride <= 0) return;

		el.scrollBy({
			left: direction === 'next' ? stride : -stride,
			behavior: 'smooth',
		});
	};

	const goToMinhasConsultas = () => navigate('/minhas-consultas');
	const goToSchedule = () => navigate(`/consulta?${searchParams.toString()}`);

	if (isLoading) {
		return (
			<div
				className="w-full space-y-4"
				data-site-tutorial="home-next-panel"
			>
				<UpcomingSessionsSectionHeader
					onViewAllConsultas={goToMinhasConsultas}
					onSchedule={goToSchedule}
				/>
				<div className={CARD_SLIDE_CLASS}>
					<UserHomeUpcomingSessionCardSkeleton />
				</div>
			</div>
		);
	}

	if (sessions.length === 0) {
		return (
			<div
				className="w-full space-y-4"
				data-site-tutorial="home-next-panel"
			>
				<UpcomingSessionsSectionHeader
					onViewAllConsultas={goToMinhasConsultas}
					onSchedule={goToSchedule}
				/>
				<div className="flex flex-col items-center justify-center rounded-[2.5rem] border border-dashed border-zinc-200 bg-white px-6 py-10 text-center shadow-sm">
					<div className="mb-3 flex size-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
						<Calendar size={22} />
					</div>
					<p className="text-sm font-semibold text-zinc-700">
						Sem próximas consultas
					</p>
					<p className="mt-1 text-sm text-zinc-500">
						Quando você agendar uma consulta, ela aparecerá aqui.
					</p>
				</div>
			</div>
		);
	}

	const showControls = sessions.length > 1 && hasOverflow;

	return (
		<div className="w-full space-y-4" data-site-tutorial="home-next-panel">
			<UpcomingSessionsSectionHeader
				onViewAllConsultas={goToMinhasConsultas}
				onSchedule={goToSchedule}
				trailing={
					showControls ? (
						<div className="flex items-center gap-1">
							<button
								type="button"
								onClick={() => scrollByStep('prev')}
								disabled={!canScrollPrev}
								aria-label="Sessão anterior"
								className={cn(
									'rounded-full p-1.5 transition-colors',
									canScrollPrev
										? 'text-zinc-500 hover:bg-zinc-100'
										: 'cursor-not-allowed text-zinc-300',
								)}
							>
								<ChevronLeft size={20} />
							</button>
							<button
								type="button"
								onClick={() => scrollByStep('next')}
								disabled={!canScrollNext}
								aria-label="Próxima sessão"
								className={cn(
									'rounded-full p-1.5 transition-colors',
									canScrollNext
										? 'text-zinc-500 hover:bg-zinc-100'
										: 'cursor-not-allowed text-zinc-300',
								)}
							>
								<ChevronRight size={20} />
							</button>
						</div>
					) : null
				}
			/>

			<div className="w-full overflow-hidden">
				<div
					ref={scrollRef}
					className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
				>
					{sessions.map((session) => (
						<div key={session.id} className={CARD_SLIDE_CLASS}>
							<UserHomeUpcomingSessionCard
								session={session}
								highlightBenefitPayment
								onPayment={onPayment}
							/>
						</div>
					))}
				</div>
			</div>

			{showControls && (
				<div className="flex w-full justify-center gap-1.5">
					{sessions.map((session, index) => (
						<button
							key={session.id}
							type="button"
							aria-label={`Ir para sessão ${index + 1}`}
							onClick={() => scrollToIndex(index)}
							className={cn(
								'size-1.5 rounded-full transition-colors',
								index === activeIndex
									? 'bg-brand-orange'
									: 'bg-zinc-300 hover:bg-zinc-400',
							)}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export { UserHomeUpcomingSessionsCarousel };
